import type { EventPriceData, EventPriceRow } from '../../types';

/**
 * Event-window interval returns ("price into and out of earnings"):
 *   into:     T-10 → T-2  (the run-up)
 *   reaction: T-2  → T+2  (the print)
 *   post:     T+2  → T+10 (the follow-through)
 *
 * Offsets are trading days relative to the event anchor (dayOffset 0).
 * An interval is null until BOTH endpoints exist within ±1 trading day of
 * their targets — a pre-earnings assignment honestly shows only the run-up.
 */
export interface EventIntervalReturns {
  into: number | null;
  reaction: number | null;
  post: number | null;
}

/** The three canonical event windows, in display order. Shared by the task
 *  header (named into/reaction/post) and the home grid's REACTION columns. */
export const RETURN_INTERVALS: [number, number][] = [[-10, -2], [-2, 2], [2, 10]];

const ENDPOINT_TOLERANCE = 1;

const validRows = (priceTable: EventPriceRow[] | null | undefined): EventPriceRow[] =>
  (priceTable ?? []).filter(
    (row): row is EventPriceRow =>
      !!row && typeof row.price === 'number' && typeof row.dayOffset === 'number',
  );

const closest = (rows: EventPriceRow[], target: number): EventPriceRow | null => {
  let best: EventPriceRow | null = null;
  let bestDist = Infinity;
  for (const row of rows) {
    const dist = Math.abs(row.dayOffset - target);
    if (dist < bestDist) {
      best = row;
      bestDist = dist;
    }
  }
  return best && bestDist <= ENDPOINT_TOLERANCE ? best : null;
};

/**
 * Fractional return over one event window (e.g. 0.119 for +11.9%).
 * Endpoints must exist within ±1 trading day of their targets; otherwise null
 * — pre-earnings rows honestly produce nulls for unprinted windows.
 */
export const computeIntervalReturn = (
  priceTable: EventPriceRow[] | null | undefined,
  from: number,
  to: number,
): number | null => {
  const rows = validRows(priceTable);
  if (rows.length === 0) return null;
  const a = closest(rows, from);
  const b = closest(rows, to);
  if (!a || !b || b.dayOffset <= a.dayOffset || a.price === 0) return null;
  return b.price / a.price - 1;
};

export const computeEventIntervals = (
  data: EventPriceData | null | undefined,
): EventIntervalReturns => {
  const [intoWindow, reactionWindow, postWindow] = RETURN_INTERVALS;
  return {
    into: computeIntervalReturn(data?.priceTable, ...intoWindow),
    reaction: computeIntervalReturn(data?.priceTable, ...reactionWindow),
    post: computeIntervalReturn(data?.priceTable, ...postWindow),
  };
};

/** Where an event sits relative to now — drives what the reaction columns
 *  may honestly show. 'imminent' ≈ within 12 trading days (16 calendar):
 *  the run-up window is live but the print hasn't happened. */
export type EventTiming = 'past' | 'imminent' | 'future' | 'none';

const IMMINENT_CALENDAR_DAYS = 16;

export const eventTiming = (
  eventDate: string | null | undefined,
  now: Date = new Date(),
): EventTiming => {
  if (!eventDate) return 'none';
  const event = new Date(eventDate);
  if (Number.isNaN(event.getTime())) return 'none';
  const diffDays = (event.getTime() - now.getTime()) / 86_400_000;
  if (diffDays <= 0) return 'past';
  if (diffDays <= IMMINENT_CALENDAR_DAYS) return 'imminent';
  return 'future';
};
